import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { draftService } from '../services/draftService';
import { supabaseDraftService } from '../services/supabaseDraftService';
import {
  serializeStoreState,
  deserializeStoreState,
  geometryToCompactData,
  compactDataToGeometry,
  serializeMaterial,
  deserializeMaterial
} from '../utils/serialization';
import { useEditorStore } from './index';
import useElongationStore from './useElongationStore';
import useWorkflowStore from './useWorkflowStore';
import useFinalizationDataStore from './useFinalizationDataStore';
import useRectificationStore from './useRectificationStore';
import useTrimlineStore from './useTrimlineStore';
import useFlaresSmootheningStore from './useFlaresSmootheningStore';
import useFlareStore from './useFlareStore';
import useThicknessStore from './useThicknessStore';
import * as THREE from 'three';

// Feature flag: Use Supabase if configured, otherwise fall back to local storage
const USE_SUPABASE = supabaseDraftService.isAvailable();

const useDraftStore = create(
  devtools((set, get) => ({
    // Draft management state
    drafts: [],
    currentDraftId: null,
    isLoading: false,
    isSaving: false,
    error: null,

    // UI state
    showSaveDialog: false,
    showLoadDialog: false,
    saveDialogData: { name: '', description: '' },
    workflowStepToNavigate: null,

    // Actions
    setShowSaveDialog: (show) => set({ showSaveDialog: show }),
    setShowLoadDialog: (show) => set({ showLoadDialog: show }),
    setSaveDialogData: (data) => set(state => ({
      saveDialogData: { ...state.saveDialogData, ...data }
    })),
    setError: (error) => set({ error }),
    clearError: () => set({ error: null }),
    clearNavigationState: () => set({ workflowStepToNavigate: null }),

    // Load drafts list
    loadDraftsList: async () => {
      set({ isLoading: true, error: null });
      try {
        const result = USE_SUPABASE
          ? await supabaseDraftService.listDrafts()
          : await draftService.listDrafts();

        if (result.success) {
          set({ drafts: result.drafts });
        } else {
          set({ error: result.error });
        }
      } catch (error) {
        set({ error: error.message });
      } finally {
        set({ isLoading: false });
      }
    },

    // Save current application state as draft
    saveDraft: async (draftName, description = '') => {
      set({ isSaving: true, error: null });
      useEditorStore.getState().setIsLoading(true, 'Saving draft...');

      console.log(`[DraftStore] Using ${USE_SUPABASE ? 'Supabase' : 'Local'} storage`);

      try {
        // Get current state from all stores
        const workflowState = useWorkflowStore.getState();
        const editorState = useEditorStore.getState();
        const elongationState = useElongationStore.getState();
        const trimlineState = useTrimlineStore.getState();
        const flaresSmootheningState = useFlaresSmootheningStore.getState();
        const flareState = useFlareStore.getState();
        const thicknessState = useThicknessStore.getState();
        const finalizationState = useFinalizationDataStore.getState();
        const sceneRefs = editorState.sceneRefs;

        console.log('=== DRAFT SAVE DEBUG ===');
        console.log('Finalization elongation data:', finalizationState.appliedTools.elongation);
        console.log('Current elongation UI state:', {
          enabled: elongationState.enabled,
          applied: elongationState.applied,
          value: elongationState.value,
          unit: elongationState.unit
        });

        // Use finalization store as authoritative source for applied tools
        const appliedElongation = finalizationState.appliedTools.elongation;
        const hasAppliedElongation = appliedElongation.applied && appliedElongation.value > 0;

        console.log('🔍 CHECKING APPLIED STATUS FROM FINALIZATION STORE:');
        console.log('  applied:', appliedElongation.applied);
        console.log('  value:', appliedElongation.value);
        console.log('  unit:', appliedElongation.unit);
        console.log('  hasAppliedElongation result:', hasAppliedElongation);

        if (!hasAppliedElongation) {
          console.warn('⚠️  Warning: No elongation appears to be applied. Saving default state.');
        }

        // Generate draft ID first
        const draftId = USE_SUPABASE ? supabaseDraftService.generateDraftId() : draftService.generateDraftId();

        // Prepare draft data with mesh data included
        const draftData = {
          workflowStep: workflowState.currentStep, // For now, focusing on elongation
          editorState: {
            history: editorState.history,
            historyIndex: editorState.historyIndex,
            activeTool: editorState.activeTool,
            showUnmodifiedMesh: editorState.showUnmodifiedMesh,
            toolSettings: editorState.toolSettings
          },
          toolStates: {
            elongation: {
              // Simple structure - only essential fields
              enabled: appliedElongation.enabled,
              value: appliedElongation.value,
              unit: 'mm',
              isElongationApplied: appliedElongation.applied
            },
            rectification: {
              measureReduction: {
                applied: finalizationState.appliedTools.rectification.measureReduction.applied,
                measurements: finalizationState.appliedTools.rectification.measureReduction.measurements,
                appliedAt: finalizationState.appliedTools.rectification.measureReduction.appliedAt
              },
              globalReduction: {
                applied: finalizationState.appliedTools.rectification.globalReduction.applied,
                value: finalizationState.appliedTools.rectification.globalReduction.value,
                valueType: finalizationState.appliedTools.rectification.globalReduction.valueType,
                unit: finalizationState.appliedTools.rectification.globalReduction.unit,
                appliedAt: finalizationState.appliedTools.rectification.globalReduction.appliedAt
              },
              scoopRecharge: {
                applied: finalizationState.appliedTools.rectification.scoopRecharge.applied,
                zones: finalizationState.appliedTools.rectification.scoopRecharge.zones,
                appliedAt: finalizationState.appliedTools.rectification.scoopRecharge.appliedAt
              },
              smoothening: {
                applied: finalizationState.appliedTools.rectification.smoothening.applied,
                zones: finalizationState.appliedTools.rectification.smoothening.zones,
                appliedAt: finalizationState.appliedTools.rectification.smoothening.appliedAt
              }
            },
            flares: {
              flare: {
                applied: finalizationState.appliedTools.flares.flare.applied,
                flareValues: finalizationState.appliedTools.flares.flare.flareValues,
                segmentHeightValues: finalizationState.appliedTools.flares.flare.segmentHeightValues,
                segmentDeflectValues: finalizationState.appliedTools.flares.flare.segmentDeflectValues,
                segmentFalloffHeightValues: finalizationState.appliedTools.flares.flare.segmentFalloffHeightValues,
                segmentFalloffDeflectValues: finalizationState.appliedTools.flares.flare.segmentFalloffDeflectValues,
                deflectBlendRatio: finalizationState.appliedTools.flares.flare.deflectBlendRatio,
                appliedAt: finalizationState.appliedTools.flares.flare.appliedAt,
              },
              smoothening: {
                applied: finalizationState.appliedTools.flares.smoothening.applied,
                zones: finalizationState.appliedTools.flares.smoothening.zones,
                appliedAt: finalizationState.appliedTools.flares.smoothening.appliedAt,
              },
              state: {
                flareValues: flareState.flareValues,
                segmentHeightValues: flareState.segmentHeightValues,
                segmentDeflectValues: flareState.segmentDeflectValues,
                segmentFalloffHeightValues: flareState.segmentFalloffHeightValues,
                segmentFalloffDeflectValues: flareState.segmentFalloffDeflectValues,
                deflectBlendRatio: flareState.deflectBlendRatio,
                selectedSegmentIndex: flareState.selectedSegmentIndex,
                isFlareApplied: flareState.isFlareApplied,
                isFlareAppliedFromDraft: flareState.isFlareAppliedFromDraft,
                smoothning: {
                  enabled: flaresSmootheningState.smoothning.enabled,
                  zones: (flaresSmootheningState.smoothning.zones || []).map((zone) => ({
                    ...zone,
                    influences: zone.influences ? Array.from(zone.influences) : zone.influences,
                  })),
                  brushRadius: flaresSmootheningState.smoothning.brushRadius,
                  previewVisible: flaresSmootheningState.smoothning.previewVisible,
                },
                isSmootheningApplied: flaresSmootheningState.isSmootheningApplied,
                isSmootheningAppliedFromDraft: flaresSmootheningState.isSmootheningAppliedFromDraft,
              },
            },
            thickness: {
              applied: finalizationState.appliedTools.thickness.applied,
              value: finalizationState.appliedTools.thickness.value,
              topValue: finalizationState.appliedTools.thickness.topValue,
              unit: finalizationState.appliedTools.thickness.unit,
              appliedAt: finalizationState.appliedTools.thickness.appliedAt,
              // Also store current UI state for restoration
              previewVisible: thicknessState.thickness.previewVisible,
              isThicknessApplied: thicknessState.isThicknessApplied,
            },
            trimline: {
              applied: finalizationState.appliedTools.trimline.applied,
              appliedAt: finalizationState.appliedTools.trimline.appliedAt,
              // Also store current UI state for restoration
              enabled: trimlineState.trimline.enabled,
              points: trimlineState.trimline.points,
              bezierHandles: trimlineState.trimline.bezierHandles,
              previewVisible: trimlineState.trimline.previewVisible,
              selectedPointIndex: trimlineState.trimline.selectedPointIndex,
              showBezierHandles: trimlineState.trimline.showBezierHandles
            }
          },
          meshData: {},
          materials: {},
          timestamp: new Date().toISOString()
        };

        // Save mesh data if available using compact format
        const { shadedMesh, unmodifiedMesh, material } = sceneRefs;

        // Prepare mesh content based on storage type
        const meshContent = {};

        if (shadedMesh && shadedMesh.geometry) {
          try {
            const compactData = geometryToCompactData(shadedMesh.geometry);

            if (USE_SUPABASE) {
              // For Supabase: prepare as JSON string for Storage upload
              meshContent.shadedMesh = JSON.stringify(compactData);
            } else {
              // For local: save file separately
              await draftService.saveModelFile(draftId, 'shaded', compactData);
              draftData.meshData.shadedMesh = `${draftId}_shaded.json`;
            }

            // Save material
            if (material) {
              console.log('Saving shaded mesh material:', material.type, 'color:', material.color.getHex());
              draftData.materials.shadedMesh = serializeMaterial(material);
              console.log('Serialized material data:', draftData.materials.shadedMesh);
            }
          } catch (error) {
            console.warn('Failed to save shaded mesh:', error);
          }
        }

        if (unmodifiedMesh && unmodifiedMesh.geometry) {
          try {
            const compactData = geometryToCompactData(unmodifiedMesh.geometry);

            if (USE_SUPABASE) {
              // For Supabase: prepare as JSON string for Storage upload
              meshContent.unmodifiedMesh = JSON.stringify(compactData);
            } else {
              // For local: save file separately
              await draftService.saveModelFile(draftId, 'unmodified', compactData);
              draftData.meshData.unmodifiedMesh = `${draftId}_unmodified.json`;
            }

            // Save material
            if (unmodifiedMesh.material) {
              draftData.materials.unmodifiedMesh = serializeMaterial(unmodifiedMesh.material);
            }
          } catch (error) {
            console.warn('Failed to save unmodified mesh:', error);
          }
        }

        // Save draft based on storage type
        let result;
        if (USE_SUPABASE) {
          // Use Supabase service (handles both DB and Storage)
          // Add mesh content for Supabase upload
          draftData.meshContent = meshContent;
          result = await supabaseDraftService.saveDraft(draftName, draftData, 'temp'); // orderId will be 'temp' until backend is ready
        } else {
          // Use local file service
          result = await draftService.saveDraftWithId(draftId, draftData, draftName);
        }

        if (result.success) {
          set({
            currentDraftId: draftId,
            showSaveDialog: false,
            saveDialogData: { name: '', description: '' }
          });

          // Refresh drafts list
          get().loadDraftsList();

          return { success: true, draftId };
        } else {
          set({ error: result.error });
          return { success: false, error: result.error };
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to save draft';
        set({ error: errorMessage });
        return { success: false, error: errorMessage };
      } finally {
        set({ isSaving: false });
        useEditorStore.getState().setIsLoading(false);
      }
    },

    // Load draft and restore application state
    loadDraft: async (draftId) => {
      set({ isLoading: true, error: null });

      console.log(`[DraftStore] Loading draft using ${USE_SUPABASE ? 'Supabase' : 'Local'} storage`);

      try {
        useEditorStore.getState().setIsLoading(true, 'Loading draft...'); // Show loading screen

        // Load draft data based on storage type
        let result;
        if (USE_SUPABASE) {
          result = await supabaseDraftService.loadDraft(draftId);
        } else {
          result = await draftService.loadDraft(draftId);
        }

        if (!result.success) {
          set({ error: result.error });
          return { success: false, error: result.error };
        }

        const draftData = result.data;

        // Get fresh references each time to avoid stale state
        let editorStore = useEditorStore.getState();
        let { scene } = editorStore.sceneRefs;
        const { replaceShadedMesh } = editorStore;

        // Load and replace the mesh first
        if (scene && (draftData.meshData?.shadedMesh || draftData.meshContent?.shadedMesh)) {
          try {
            let meshContent;

            if (USE_SUPABASE) {
              // For Supabase: mesh content is already in draftData
              meshContent = JSON.parse(draftData.meshContent.shadedMesh);
            } else {
              // For local: load from file
              const meshResult = await draftService.loadModelFile(draftId, 'shaded');
              if (!meshResult.success) {
                throw new Error('Failed to load mesh file');
              }
              meshContent = meshResult.content;
            }

            const geometry = compactDataToGeometry(meshContent);

            // Create default material - check if geometry has vertex colors
            const hasVertexColors = geometry.attributes.color !== undefined;

            let material = new THREE.MeshLambertMaterial({
              color: hasVertexColors ? 0xffffff : 0xE8D5B7, // White if vertex colors, skin tone if not
              side: THREE.DoubleSide,
              vertexColors: hasVertexColors // Enable vertex colors if available
            });

            // Restore material if available
            if (draftData.materials?.shadedMesh) {
              const restoredMaterial = deserializeMaterial(draftData.materials.shadedMesh);
              if (restoredMaterial) {
                material = restoredMaterial;
                // Ensure vertex colors are enabled if geometry has them
                if (hasVertexColors) {
                  material.vertexColors = true;
                }
              } else {
                console.warn('Failed to restore material, using default');
              }
            }

            material.needsUpdate = true;

            const mesh = new THREE.Mesh(geometry, material);
            mesh.name = 'ShadedMesh Restored from Draft';
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            replaceShadedMesh(mesh);

            // Wait for state update to propagate and verify it worked
            await new Promise(resolve => setTimeout(resolve, 50));

            // Get fresh state after replacement
            editorStore = useEditorStore.getState();

            // Verify the replacement worked
            if (editorStore.sceneRefs.shadedMesh?.name !== mesh.name) {
              // Force update if needed
              useEditorStore.setState({
                sceneRefs: { ...editorStore.sceneRefs, shadedMesh: mesh }
              });
            }
          } catch (error) {
            console.warn('Failed to load shaded mesh:', error);
          }
        }

        // Load unmodified mesh if exists
        if (scene && (draftData.meshData?.unmodifiedMesh || draftData.meshContent?.unmodifiedMesh)) {
          try {
            let meshContent;

            if (USE_SUPABASE) {
              // For Supabase: mesh content is already in draftData
              if (draftData.meshContent.unmodifiedMesh) {
                meshContent = JSON.parse(draftData.meshContent.unmodifiedMesh);
              }
            } else {
              // For local: load from file
              const meshResult = await draftService.loadModelFile(draftId, 'unmodified');
              if (meshResult.success) {
                meshContent = meshResult.content;
              }
            }

            if (meshContent) {
              const geometry = compactDataToGeometry(meshContent);

              let material = new THREE.MeshLambertMaterial({
                color: 0xA0A0A0, // Gray color for unmodified mesh
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.5
              });

              // Restore material if available
              if (draftData.materials?.unmodifiedMesh) {
                const restoredMaterial = deserializeMaterial(draftData.materials.unmodifiedMesh);
                if (restoredMaterial) {
                  material = restoredMaterial;
                }
              }

              const mesh = new THREE.Mesh(geometry, material);
              mesh.name = 'UnmodifiedMesh Restored';
              mesh.visible = false; // Hidden by default
              mesh.castShadow = false;
              mesh.receiveShadow = false;
              scene.add(mesh);

              // Update scene refs
              const { setSceneRefs } = editorStore;
              setSceneRefs({
                ...editorStore.sceneRefs,
                unmodifiedMesh: mesh
              });
            }
          } catch (error) {
            console.warn('Failed to load unmodified mesh:', error);
          }
        }

        // Wait for mesh processing
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Get fresh state after mesh replacement
        const currentState = useEditorStore.getState();
        const currentShadedMesh = currentState.sceneRefs.shadedMesh;
        const currentScene = currentState.sceneRefs.scene;
        const sceneChildren = currentScene.children;

        // Remove any stale meshes that might still be in the scene
        // Look for meshes that are NOT the current shaded mesh and contain mesh-like names
        const staleMeshes = sceneChildren.filter(child => {
          // Skip if it's the current shaded mesh
          if (child === currentShadedMesh) return false;

          // Look for old mesh patterns (PLYMesh, ShadedMesh, etc.) but exclude our new one
          const hasMeshPattern = child.name && (
            child.name.includes('PLYMesh') ||
            child.name.includes('ShadedMesh') ||
            child.name.includes('Mesh')
          );

          // Don't remove unmodified mesh or other specific meshes
          const isUnmodifiedMesh = child.name && child.name.includes('UnmodifiedMesh');
          const isNewRestoredMesh = child.name === 'ShadedMesh Restored from Draft';

          return hasMeshPattern && !isUnmodifiedMesh && !isNewRestoredMesh;
        });

        if (staleMeshes.length > 0) {
          staleMeshes.forEach(mesh => {
            currentScene.remove(mesh);
            mesh.geometry?.dispose?.();
            mesh.material?.dispose?.();
          });
        }

        // Restore elongation tool state
        if (draftData.toolStates.elongation) {
          const elongationData = draftData.toolStates.elongation;

          useElongationStore.setState({
            enabled: elongationData.enabled || false,
            value: elongationData.value || 0,
            isElongationApplied: elongationData.isElongationApplied || false,
            isAppliedFromDraft: true,
            currentElongationCommand: null,
            previewMesh: null // Will be recreated with new mesh
          });

          // Restore finalization store data
          if (elongationData.isElongationApplied) {
            useFinalizationDataStore.getState().saveElongation(
              elongationData.value,
              elongationData.unit
            );
          }
        }

        // Restore trimline tool state
        if (draftData.toolStates.trimline) {
          const trimlineData = draftData.toolStates.trimline;

          // Restore the UI state
          useTrimlineStore.setState({
            trimline: {
              enabled: trimlineData.enabled || false,
              points: trimlineData.points || [],
              bezierHandles: trimlineData.bezierHandles || [],
              selectedPointIndex: trimlineData.selectedPointIndex || null,
              showBezierHandles: trimlineData.showBezierHandles || false,
              previewVisible: trimlineData.previewVisible || false
            },
            applied: trimlineData.applied || false,
            isTrimlineApplied: trimlineData.applied || false,
            isAppliedFromDraft: true,
            currentTrimlineCommand: null,
            previewMesh: null, // Will be recreated with new mesh
            upperMesh: null // Will be restored separately
          });

          // Restore finalization store data
          if (trimlineData.applied) {
            useFinalizationDataStore.getState().saveTrimline();
          }
        }

        // Restore rectification tool states
        if (draftData.toolStates.rectification) {
          const rectificationData = draftData.toolStates.rectification;
          const rectificationStore = useRectificationStore.getState();
          console.log('Restoring rectification:', rectificationData);

          // Restore measure reduction
          if (rectificationData.measureReduction?.applied) {
            rectificationStore.setIsRectificationApplied(true);
            rectificationStore.setIsRectificationAppliedFromDraft(true);
            rectificationStore.setCurrentRectificationCommand(null);

            useFinalizationDataStore.getState().saveRectificationMeasureReduction(
              rectificationData.measureReduction.measurements
            );
          }

          // Restore global reduction
          if (rectificationData.globalReduction?.applied) {
            rectificationStore.setIsGlobalReductionApplied(true);
            rectificationStore.setIsGlobalReductionAppliedFromDraft(true);
            rectificationStore.setCurrentGlobalReductionCommand(null);
            rectificationStore.setGlobalReduction({
              value: rectificationData.globalReduction.value,
              valueType: rectificationData.globalReduction.valueType,
              unit: rectificationData.globalReduction.unit
            });

            useFinalizationDataStore.getState().saveRectificationGlobalReduction(
              rectificationData.globalReduction.value,
              rectificationData.globalReduction.valueType,
              rectificationData.globalReduction.unit
            );
          }

          // Restore scoop/recharge
          if (rectificationData.scoopRecharge?.applied) {
            rectificationStore.setIsScoopRechargeApplied(true);
            rectificationStore.setIsScoopRechargeAppliedFromDraft(true);
            rectificationStore.setCurrentScoopRechargeCommand(null);

            useFinalizationDataStore.getState().saveRectificationScoopRecharge(
              rectificationData.scoopRecharge.zones
            );
          }

          // Restore smoothening
          if (rectificationData.smoothening?.applied) {
            rectificationStore.setIsSmootheningApplied(true);
            rectificationStore.setIsSmootheningAppliedFromDraft(true);
            rectificationStore.setCurrentSmootheningCommand(null);

            useFinalizationDataStore.getState().saveRectificationSmoothening(
              rectificationData.smoothening.zones
            );
          }
        }

        // Restore flares tool states
        if (draftData.toolStates.flares) {
          const flaresData = draftData.toolStates.flares;
          const baseState = useFlaresSmootheningStore.getState();
          const flareBaseState = useFlareStore.getState();

          // Restore main flare state
          if (flaresData.state) {
            useFlareStore.setState({
              flareValues: flaresData.state.flareValues ?? flareBaseState.flareValues,
              segmentHeightValues: flaresData.state.segmentHeightValues ?? flareBaseState.segmentHeightValues,
              segmentDeflectValues: flaresData.state.segmentDeflectValues ?? flareBaseState.segmentDeflectValues,
              segmentFalloffHeightValues: flaresData.state.segmentFalloffHeightValues ?? flareBaseState.segmentFalloffHeightValues,
              segmentFalloffDeflectValues: flaresData.state.segmentFalloffDeflectValues ?? flareBaseState.segmentFalloffDeflectValues,
              deflectBlendRatio: flaresData.state.deflectBlendRatio ?? flareBaseState.deflectBlendRatio,
              selectedSegmentIndex: flaresData.state.selectedSegmentIndex ?? flareBaseState.selectedSegmentIndex,
              isFlareApplied: flaresData.state.isFlareApplied ?? false,
              isFlareAppliedFromDraft: true,
              currentFlareCommand: null,
              previewMesh: null,
              baseGeometry: null,
            });
          }

          // Restore main flare application state to finalization store
          if (flaresData.flare?.applied) {
            useFlareStore.getState().setFlareApplied(true);
            useFlareStore.getState().setFlareAppliedFromDraft(true);
            useFlareStore.getState().setCurrentFlareCommand(null);

            useFinalizationDataStore.getState().saveFlare({
              flareValues: flaresData.flare.flareValues,
              segmentHeightValues: flaresData.flare.segmentHeightValues,
              segmentDeflectValues: flaresData.flare.segmentDeflectValues,
              segmentFalloffHeightValues: flaresData.flare.segmentFalloffHeightValues,
              segmentFalloffDeflectValues: flaresData.flare.segmentFalloffDeflectValues,
              deflectBlendRatio: flaresData.flare.deflectBlendRatio,
            });
          }

          // Restore flares smoothening state
          if (flaresData.state?.smoothning) {
            const restoredZones = (flaresData.state.smoothning.zones || []).map((zone) => ({
              ...zone,
              influences: zone.influences ? Float32Array.from(zone.influences) : zone.influences,
            }));

            useFlaresSmootheningStore.setState({
              smoothning: {
                enabled: flaresData.state.smoothning.enabled ?? baseState.smoothning.enabled,
                zones: restoredZones,
                brushRadius: flaresData.state.smoothning.brushRadius ?? baseState.smoothning.brushRadius,
                previewVisible: flaresData.state.smoothning.previewVisible ?? baseState.smoothning.previewVisible,
                previewMesh: null,
              },
              isSmootheningApplied: flaresData.state.isSmootheningApplied ?? false,
              isSmootheningAppliedFromDraft: flaresData.state.isSmootheningAppliedFromDraft ?? false,
              currentSmootheningCommand: null,
            });
          }

          if (flaresData.smoothening?.applied) {
            useFlaresSmootheningStore.getState().setIsSmootheningApplied(true);
            useFlaresSmootheningStore.getState().setIsSmootheningAppliedFromDraft(true);
            useFlaresSmootheningStore.getState().setCurrentSmootheningCommand(null);

            useFinalizationDataStore.getState().saveFlaresSmoothening(
              flaresData.smoothening.zones
            );
          }
        }

        // Restore thickness state
        if (draftData.toolStates.thickness) {
          const thicknessData = draftData.toolStates.thickness;

          // Restore UI state
          useThicknessStore.setState({
            thickness: {
              value: thicknessData.value || 0,
              topValue: thicknessData.topValue || 0,
              unit: thicknessData.unit || 'mm',
              previewVisible: thicknessData.previewVisible || false,
            },
            isThicknessApplied: thicknessData.isThicknessApplied || false,
            isThicknessAppliedFromDraft: true,
            currentThicknessCommand: null,
            previewMesh: null,
          });

          // Restore finalization store data
          if (thicknessData.applied) {
            useFinalizationDataStore.getState().saveThickness(
              thicknessData.value,
              thicknessData.topValue,
              thicknessData.unit
            );
          }
        }

        // Restore basic editor state
        if (draftData.editorState) {
          const { activeTool, showUnmodifiedMesh } = draftData.editorState;

          useEditorStore.setState({
            history: [],
            historyIndex: -1,
            activeTool: activeTool || 'none',
            showUnmodifiedMesh: showUnmodifiedMesh || false
          });
        }

        // Set the correct active step from the draft
        if (draftData.workflowStep) {
          useWorkflowStore.setState({
            currentStep: draftData.workflowStep,
            currentWorkflow: 'MAIN_PROCESS' // Assuming main process for now
          });
        }

        // Wait a final moment for all React updates to process
        await new Promise(resolve => setTimeout(resolve, 200));

        // Final verification of the loaded state
        const finalState = useEditorStore.getState();
        const finalShadedMesh = finalState.sceneRefs.shadedMesh;

        set({
          currentDraftId: draftId,
          showLoadDialog: false,
          workflowStepToNavigate: draftData.workflowStep
        });

        return { success: true };
      } catch (error) {
        const errorMessage = error.message || 'Failed to load draft';
        console.error('❌ Draft loading failed:', error);
        set({ error: errorMessage });
        return { success: false, error: errorMessage };
      } finally {
        // Disable loading screen
        useEditorStore.getState().setIsLoading(false);
        set({ isLoading: false });
      }
    },

    // Delete draft
    deleteDraft: async (draftId) => {
      set({ isLoading: true, error: null });
      useEditorStore.getState().setIsLoading(true, 'Deleting draft...');

      console.log(`[DraftStore] Deleting draft using ${USE_SUPABASE ? 'Supabase' : 'Local'} storage`);

      try {
        let result;

        if (USE_SUPABASE) {
          // Supabase service handles both Storage files and DB record deletion
          result = await supabaseDraftService.deleteDraft(draftId);
        } else {
          // Local storage: manually delete model files then draft
          const draftResult = await draftService.loadDraft(draftId);

          // Delete model files if draft data exists
          if (draftResult.success && draftResult.data.meshData) {
            const meshData = draftResult.data.meshData;

            // Delete each model file
            const modelFiles = [
              meshData.shadedMesh,
              meshData.unmodifiedMesh
            ].filter(Boolean);

            for (const modelFile of modelFiles) {
              try {
                const modelName = modelFile.replace(`${draftId}_`, '').replace('.json', '');
                await draftService.deleteModelFile(draftId, modelName);
              } catch (error) {
                console.warn('Failed to delete model file:', modelFile, error);
              }
            }
          }

          // Then delete the main draft file
          result = await draftService.deleteDraft(draftId);
        }

        if (result.success) {
          // Refresh drafts list
          get().loadDraftsList();
          return { success: true };
        } else {
          set({ error: result.error });
          return { success: false, error: result.error };
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to delete draft';
        set({ error: errorMessage });
        return { success: false, error: errorMessage };
      } finally {
        set({ isLoading: false });
        useEditorStore.getState().setIsLoading(false);
      }
    },

    // Reset store
    reset: () => set({
      drafts: [],
      currentDraftId: null,
      isLoading: false,
      isSaving: false,
      error: null,
      showSaveDialog: false,
      showLoadDialog: false,
      saveDialogData: { name: '', description: '' }
    })
  }))
);

export default useDraftStore;